import React, { createContext, useContext, useMemo } from 'react';
import { useLifely } from './LifelyContext';

// Pages always accessible to any authenticated user regardless of plan
const ALWAYS_ACCESSIBLE = ['/settings'];

// Pages accessible on the free plan
const FREE_PLAN_PATHS = ['/', '/insights'];

const PlanContext = createContext(null);

export const usePlan = () => {
  const context = useContext(PlanContext);
  if (!context) {
    throw new Error('usePlan must be used within a PlanProvider');
  }
  return context;
};

export const PlanProvider = ({ children }) => {
  const { user, isAuthenticated } = useLifely();

  const planInfo = useMemo(() => {
    if (!user || !isAuthenticated) {
      return {
        activePlans: [],
        hasInsightsPlan: false,
        hasStandardPlan: false,
        hasPlusPlan: false,
        isTrial: false,
        isExpired: false,
        hasAnyPlan: false,
        hasFullAccess: false,
        planName: null,
      };
    }

    const plan = user.plan || 'free';

    const hasInsightsPlan = plan === 'free';
    const hasStandardPlan = plan === 'standard';
    const hasPlusPlan = plan === 'plus';
    const isTrial = plan === 'trial';
    const isExpired = plan === 'expired';
    const hasAnyPlan = true;
    // Trial users get the full experience; expired users are locked out.
    const hasFullAccess = hasStandardPlan || hasPlusPlan || isTrial;

    let planName = null;
    if (hasPlusPlan) planName = 'Plus';
    else if (hasStandardPlan) planName = 'Standard';
    else if (isTrial) planName = 'Trial';
    else if (isExpired) planName = 'Expired';
    else if (hasInsightsPlan) planName = 'Free';

    return {
      activePlans: [plan],
      hasInsightsPlan,
      hasStandardPlan,
      hasPlusPlan,
      isTrial,
      isExpired,
      hasAnyPlan,
      hasFullAccess,
      planName,
    };
  }, [user, isAuthenticated]);

  const canAccessPath = (path) => {
    if (!isAuthenticated || !planInfo.hasAnyPlan) {
      return false;
    }

    if (ALWAYS_ACCESSIBLE.includes(path)) {
      return true;
    }

    // Expired trial: locked to ALWAYS_ACCESSIBLE only
    if (planInfo.isExpired) {
      return false;
    }

    if (planInfo.hasFullAccess) {
      return true;
    }

    // Free plan: only specific paths
    for (const allowedPath of FREE_PLAN_PATHS) {
      if (allowedPath === '/') {
        if (path === '/') return true;
      } else if (path === allowedPath || path.startsWith(allowedPath + '/')) {
        return true;
      }
    }

    return false;
  };

  // Opens the in-app upgrade modal (a global host in Layout listens for this event).
  const openPlansModal = (trigger = 'upgrade_prompt') => {
    window.dispatchEvent(new CustomEvent('lifely:open-upgrade', { detail: { trigger } }));
  };

  const value = {
    ...planInfo,
    canAccessPath,
    openPlansModal,
  };

  return (
    <PlanContext.Provider value={value}>
      {children}
    </PlanContext.Provider>
  );
};

export default PlanContext;
